import type {
  CmmdProviderAccountInventoryAccount,
  CmmdProviderUsageSnapshot,
  CmmdProviderUsageWindow,
} from "@cmmd-forge/contracts";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import { COARSE_POINTER_HIT_AREA_CLASS } from "../../lib/touchTarget";
import { formatProviderPlanLabel } from "../settings/providerAccountState";
import { Avatar } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";

/**
 * The account list the strip opens, and the row primitives it draws with.
 *
 * The list owns these primitives because it is where each of them is used
 * most; ProviderAccountPicker imports the handful its own one-line strip
 * needs. Splitting them out keeps the picker inside the 300-line ceiling in
 * `.agents/rules/frontend-architecture.md`.
 */

export function accountLabel(account: CmmdProviderAccountInventoryAccount): string {
  return account.accountDisplayName || account.accountEmail || "Connected account";
}

export function accountKey(account: CmmdProviderAccountInventoryAccount): string {
  return `${account.provider}:${account.providerAccountId}`;
}

function limitingWindow(snapshot: CmmdProviderUsageSnapshot | null): {
  readonly label: "5h" | "7d";
  readonly window: CmmdProviderUsageWindow;
} | null {
  const windows = [
    snapshot?.fiveHour ? { label: "5h" as const, window: snapshot.fiveHour } : null,
    snapshot?.sevenDay ? { label: "7d" as const, window: snapshot.sevenDay } : null,
  ].filter(
    (entry): entry is { readonly label: "5h" | "7d"; readonly window: CmmdProviderUsageWindow } =>
      entry !== null,
  );
  return (
    windows.toSorted((left, right) => right.window.usedPercent - left.window.usedPercent)[0] ?? null
  );
}

export function remainingPercentFor(snapshot: CmmdProviderUsageSnapshot | null): number | null {
  const limiting = limitingWindow(snapshot);
  return limiting === null ? null : Math.max(0, 100 - limiting.window.usedPercent);
}

function compactResetLabel(resetsAt: string): string {
  const diffMinutes = Math.max(0, Math.round((new Date(resetsAt).getTime() - Date.now()) / 60_000));
  if (diffMinutes < 60) return `resets in ${diffMinutes}m`;
  const hours = Math.round(diffMinutes / 60);
  if (hours < 48) return `resets in ${hours}h`;
  return `resets in ${Math.round(hours / 24)}d`;
}

function AccountUsage(props: { readonly snapshot: CmmdProviderUsageSnapshot | null }) {
  const limiting = limitingWindow(props.snapshot);
  if (limiting === null) {
    return <span className="text-[11px] text-muted-foreground/70">Usage unavailable</span>;
  }

  const remainingPercent = Math.max(0, 100 - limiting.window.usedPercent);
  return (
    <div className="mt-1 flex items-center gap-2">
      <span className="shrink-0 text-[11px] font-medium tabular-nums text-foreground">
        {remainingPercent}% left
      </span>
      <span
        className="h-1 min-w-12 flex-1 overflow-hidden rounded-full bg-muted"
        aria-hidden="true"
      >
        <span
          className={cn(
            "block h-full rounded-full",
            remainingPercent <= 20 ? "bg-warning" : "bg-primary",
          )}
          style={{ width: `${remainingPercent}%` }}
        />
      </span>
      <span className="shrink-0 text-[11px] text-muted-foreground/70">
        {limiting.label} · {compactResetLabel(limiting.window.resetsAt)}
      </span>
    </div>
  );
}

/**
 * Avatar with a thin ring around it showing remaining usage. Initials-only:
 * the provider-account inventory has no avatar image URL
 * (packages/contracts/src/cmmd-generated.ts, `/providers/accounts` schema).
 */
export function AccountAvatar(props: {
  readonly name: string;
  readonly remaining: number | null;
  readonly size?: number;
}) {
  const size = props.size ?? 24;
  const radius = size / 2 - 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const remaining = props.remaining ?? 0;
  const dash = (remaining / 100) * circumference;
  return (
    <span
      className="relative flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg
        aria-hidden="true"
        className="absolute inset-0 -rotate-90"
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        width={size}
      >
        <circle
          cx={center}
          cy={center}
          fill="none"
          r={radius}
          stroke="currentColor"
          className="text-muted"
          strokeWidth="1.5"
        />
        {props.remaining !== null ? (
          <circle
            cx={center}
            cy={center}
            fill="none"
            r={radius}
            stroke="currentColor"
            className={remaining <= 20 ? "text-warning" : "text-primary"}
            strokeDasharray={`${dash} ${circumference}`}
            strokeLinecap="round"
            strokeWidth="1.5"
          />
        ) : null}
      </svg>
      {/* Avatar's own base class is a fixed size-6; size-auto lets the 3px
          inset (not a fixed width/height) determine its size here instead,
          so it scales with the ring regardless of this control's own size. */}
      <Avatar name={props.name} className="absolute inset-[3px] size-auto" />
    </span>
  );
}

export function PlanBadge(props: {
  readonly planType: string | null;
  readonly className?: string;
}) {
  const label = formatProviderPlanLabel(props.planType);
  if (label === null) return null;
  return (
    <Badge className={cn("shrink-0", props.className)} size="sm" variant="secondary">
      {label}
    </Badge>
  );
}

/**
 * One account in the flyout. An account whose credential stopped working is
 * listed rather than hidden (#3335). Hiding it left the user with an account
 * that had silently vanished and no way back.
 *
 * The identity button announces `aria-disabled` rather than carrying the
 * `disabled` attribute, so a keyboard user still reaches the row and hears why
 * it cannot be picked; the click handler is the thing that refuses. The way
 * out is a sibling action, not a nested one, because an anchor inside a button
 * is invalid and loses both semantics. "Reconnect required" is the same
 * wording the Settings > Providers row uses, which is where the action lands.
 */
function AccountRow(props: {
  readonly account: CmmdProviderAccountInventoryAccount;
  readonly selected: boolean;
  readonly snapshot: CmmdProviderUsageSnapshot | null;
  readonly onSelect: () => void;
}) {
  const needsReconnect = props.account.needsReconnect;
  return (
    <div className="flex items-center gap-2" data-slot="account-row">
      <button
        type="button"
        className={cn(
          "flex min-h-touch min-w-0 flex-1 items-center gap-2 rounded-[var(--control-radius)] px-3 py-2 text-left transition-colors md:min-h-10",
          needsReconnect
            ? "cursor-not-allowed text-muted-foreground opacity-60"
            : props.selected
              ? "bg-accent text-foreground"
              : "text-muted-foreground hover:bg-muted",
        )}
        aria-disabled={needsReconnect ? true : undefined}
        aria-pressed={needsReconnect ? undefined : props.selected}
        onClick={() => {
          if (needsReconnect) return;
          props.onSelect();
        }}
      >
        <AccountAvatar
          name={accountLabel(props.account)}
          remaining={remainingPercentFor(props.snapshot)}
          size={28}
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="block truncate text-sm font-medium text-foreground md:text-xs">
              {accountLabel(props.account)}
            </span>
            <PlanBadge planType={props.account.planType} />
          </span>
          {needsReconnect ? (
            <span className="mt-1 block text-[11px] text-warning-foreground">
              Reconnect required
            </span>
          ) : (
            <AccountUsage snapshot={props.snapshot} />
          )}
        </span>
      </button>
      {needsReconnect ? (
        <Button
          aria-label={`Reconnect ${accountLabel(props.account)}`}
          // The size variant sets a fixed height, so the touch minimum and the
          // desktop row height are restated here the way the row itself states
          // them. Without that the action measures 32px next to a 48px row.
          className="mr-2 min-h-touch shrink-0 md:min-h-10"
          render={<Link to="/settings/providers" />}
          size="sm"
          variant="ghost"
        >
          Reconnect
        </Button>
      ) : null}
    </div>
  );
}

export const STRIP_ROW_CLASS = cn(
  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-muted-foreground",
  COARSE_POINTER_HIT_AREA_CLASS,
);

/** The alternate-account list, anchored above the strip in a nested Base UI
 * popover (there is no room below without covering the search box and model
 * list). A real Base UI popover, not a hand-rolled portal, so outside-click,
 * Escape, and repositioning come for free and a click inside it does not
 * register as "outside" the picker it is nested in. */
export function AccountFlyout(props: {
  readonly accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly selectedAccount: CmmdProviderAccountInventoryAccount | undefined;
  readonly usageByAccountId: ReadonlyMap<string, CmmdProviderUsageSnapshot | null>;
  readonly onSelect: (account: CmmdProviderAccountInventoryAccount) => void;
  readonly children: ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger
        className={cn(STRIP_ROW_CLASS, "transition-colors hover:bg-muted")}
        render={<button type="button" />}
      >
        {props.children}
      </PopoverTrigger>
      <PopoverPopup align="start" className="min-w-(--anchor-width) p-1" side="top" sideOffset={4}>
        <div className="grid gap-0.5">
          {props.accounts.map((account) => (
            <AccountRow
              key={account.providerAccountId}
              account={account}
              selected={account.providerAccountId === props.selectedAccount?.providerAccountId}
              snapshot={props.usageByAccountId.get(accountKey(account)) ?? null}
              onSelect={() => props.onSelect(account)}
            />
          ))}
        </div>
      </PopoverPopup>
    </Popover>
  );
}
